"use client";

import { Elements } from "@stripe/react-stripe-js";
import { loadStripe, StripeElementsOptions } from "@stripe/stripe-js";

import { PublicConfig } from "apps/website/config/public";

import { FONTS } from "./constants";
import { PayPalExpressCheckoutInner } from "./PayPalExpressCheckoutInner";
import { CreateBasketResult } from "./types";

const stripePromise = loadStripe(PublicConfig.NEXT_PUBLIC_STRIPE_PUBLIC_KEY);

/** Version-agnostic shell around Stripe's `<Elements>` provider and the
 * Express Checkout button. Receives `createBasket` from a version-specific
 * wrapper (`<PayPalExpressCheckoutV1>` / `<PayPalExpressCheckoutV2>`) so
 * everything inside `<Elements>` (Stripe options, button styling, confirm
 * flow) lives in exactly one place. */
export function PayPalExpressCheckoutBase({
  price,
  createBasket,
  onSuccess,
  onError,
  onAvailabilityChange,
}: Readonly<{
  price: number;
  createBasket: () => Promise<CreateBasketResult>;
  onSuccess: () => void;
  onError: (message: string) => void;
  onAvailabilityChange?: (available: boolean) => void;
}>) {
  // Automatic mode (no `paymentMethodTypes`): Stripe.js picks the available
  // PMs based on the merchant's Payment Method Configuration. We then restrict
  // the *visible* express buttons down to PayPal-only inside
  // `<ExpressCheckoutInner>` via `options.paymentMethods`. This must match the
  // back-end PI, which is also automatic mode (`createPaymentIntent` with no
  // `payment_method_types`). Root-level `setupFutureUsage` is required by
  // Express Checkout's confirm-time validator for recurring PayPal; if the
  // PayPal button hides because of this, the merchant's PayPal account is
  // failing Stripe's "Express Checkout off-session" capability check (PayPal
  // Billing Agreements / Reference Transactions must be enabled).
  const elementsOptions: StripeElementsOptions = {
    fonts: FONTS,
    mode: "payment",
    amount: price,
    currency: "gbp",
    setupFutureUsage: "off_session",
  };

  return (
    <div id="StripeExpressCheckout" className="w-full">
      <Elements stripe={ stripePromise } options={ elementsOptions }>
        <PayPalExpressCheckoutInner
          createBasket={ createBasket }
          onSuccess={ onSuccess }
          onError={ onError }
          onAvailabilityChange={ onAvailabilityChange }
        />
      </Elements>
    </div>
  );
}
